import { Injectable } from '@angular/core'

import { Apollo } from 'apollo-angular'
import { Observable, catchError, forkJoin, map, throwError } from 'rxjs'

import {
  GetTrygroupDashboardPriorityListGQL,
  GetTrygroupDashboardPriorityListQuery,
  GetTrygroupDashboardPriorityListQueryVariables,
  TrygroupDashboardPriorityListFilterInput,
  TrygroupSegment as GqlTrygroupSegment,
} from '../../../_generated/graphql'
import { throwIfCustomError } from '../../util/throwIfCustomError'

export const TRYGROUP_PRIORITY_LIST_DEFAULT_LIMIT = 50
// backend MAX_LIMIT=200 と揃える。比較元はマネジメント画面のため
// 通常の 1 ページ取得で十分（dev 規模 31 user、想定 670 user は follow-up で cursor 連続取得）
// TODO(follow-up): 現在の forkJoin 2-query + client-side userId join には
// 3 症状あり、backend join に寄せれば同時解消する:
//   ① 200 user 超で delta が無音欠落 (hasNextPage console.error 警告のみ)
//   ② filter 不整合: current は filter 適用、comparison は全件取得 (filter 緩和) のため、
//      comparison 側に該当 user が居ない場合 join 失敗 → 670 user 未満でも発生しうる
//   ③ cursor で次ページ取得時に comparison 200 件も毎回 refetch される無駄
// 対応案:
//   - backend に getComparisonScoresByUserIds(userIds: [String!]!) 専用軽量 query 追加
//   - or resolver で current + comparison を server side join
const TRYGROUP_PRIORITY_LIST_COMPARISON_LIMIT = 200

export const TRYGROUP_SEGMENTS = ['S', 'A', 'B', 'N'] as const

export type TrygroupSegment = (typeof TRYGROUP_SEGMENTS)[number]

export type TrygroupPriorityListFilter = {
  segments: TrygroupSegment[]
  userCategoryIds: string[]
  requiresGuidanceOnly: boolean
  departmentId: string | null
  searchKeyword: string
}

export type TrygroupPriorityListUserCategory = {
  id: string
  name: string
  active: boolean
}

export type TrygroupPriorityListDepartment = {
  id: string
  name: string
}

export type TrygroupPriorityListMetricKey =
  | 'overallScore'
  | 'ka1'
  | 'ka2'
  | 'ka3'
  | 'ka4'
  | 'ka5'
  | 'ka6'
  | 'ka7'
  | 'ka8'
  | 'admissionRate'

export type TrygroupPriorityListDeltaDirection = 'up' | 'down' | 'flat' | 'none'

export type TrygroupPriorityListDelta = {
  value: number | null
  direction: TrygroupPriorityListDeltaDirection
}

export type TrygroupPriorityListDeltas = Record<
  TrygroupPriorityListMetricKey,
  TrygroupPriorityListDelta
>

export type TrygroupPriorityListKeyActionScores = {
  ka1: number | null
  ka2: number | null
  ka3: number | null
  ka4: number | null
  ka5: number | null
  ka6: number | null
  ka7: number | null
  ka8: number | null
}

export type TrygroupPriorityListItem = {
  userId: string
  user: {
    id: string
    name: string
    email: string
  }
  userCategoryId: string | null
  userCategoryName: string | null
  segment: TrygroupSegment | null
  overallScore: number | null
  keyActionScores: TrygroupPriorityListKeyActionScores
  admissionRate: number | null
  assessmentCount: number
  requiresGuidance: boolean
  deltas: TrygroupPriorityListDeltas
}

export type TrygroupPriorityListPageInfo = {
  hasNextPage: boolean
  endCursor: string | null
}

export type TrygroupPriorityListPage = {
  snapshotDate: string
  comparisonSnapshotDate: string
  items: TrygroupPriorityListItem[]
  totalCount: number
  pageInfo: TrygroupPriorityListPageInfo
  userCategories: TrygroupPriorityListUserCategory[]
  departments: TrygroupPriorityListDepartment[]
}

export type GetTrygroupPriorityListParams = {
  snapshotDate: Date
  /** 指定があればその日付を比較元として使う。省略時は getComparisonSnapshotDate で自動計算（月初固定）。 */
  comparisonSnapshotDate?: Date
  filter: TrygroupPriorityListFilter
  cursor?: string | null
  limit?: number
}

type GqlPriorityListItem =
  GetTrygroupDashboardPriorityListQuery['trygroupDashboardPriorityList']['items'][number]

@Injectable({
  providedIn: 'root',
})
export class TrygroupDashboardPriorityListService {
  constructor(
    private getTrygroupDashboardPriorityListGQL: GetTrygroupDashboardPriorityListGQL,
  ) {}

  getPriorityList(
    params: GetTrygroupPriorityListParams,
  ): Observable<TrygroupPriorityListPage> {
    const snapshotDate = formatSnapshotDate(params.snapshotDate)
    const comparisonSnapshotDate = formatSnapshotDate(
      params.comparisonSnapshotDate ??
        getComparisonSnapshotDate(params.snapshotDate),
    )
    const currentVariables = buildPriorityListVariables({
      snapshotDate,
      filter: buildCurrentFilterInput(params.filter),
      cursor: params.cursor,
      limit:
        params.limit == null
          ? TRYGROUP_PRIORITY_LIST_DEFAULT_LIMIT
          : params.limit,
    })
    const comparisonVariables = buildPriorityListVariables({
      snapshotDate: comparisonSnapshotDate,
      filter: buildComparisonFilterInput(params.filter),
      cursor: null,
      limit: TRYGROUP_PRIORITY_LIST_COMPARISON_LIMIT,
    })

    return forkJoin({
      current: this.fetchPriorityList(currentVariables),
      comparison: this.fetchPriorityList(comparisonVariables),
    }).pipe(
      map(({ current, comparison }) =>
        mapPriorityListPage(current, comparison, comparisonSnapshotDate),
      ),
      catchError((error: unknown) => {
        throwIfCustomError(error)
        return throwError(() => error)
      }),
    )
  }

  private fetchPriorityList(
    variables: GetTrygroupDashboardPriorityListQueryVariables,
  ): Observable<Apollo.QueryResult<GetTrygroupDashboardPriorityListQuery>> {
    return this.getTrygroupDashboardPriorityListGQL.fetch({
      variables,
      fetchPolicy: 'no-cache',
    })
  }
}

export function createDefaultPriorityListFilter(): TrygroupPriorityListFilter {
  return {
    segments: [],
    userCategoryIds: [],
    requiresGuidanceOnly: false,
    departmentId: null,
    searchKeyword: '',
  }
}

/** 表示日のデフォルトは「昨日」（日次バッチで前日分が翌朝集計される運用に揃える）。 */
export function getDefaultSnapshotDate(now: Date = new Date()): Date {
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  return yesterday
}

export function getComparisonSnapshotDate(current: Date): Date {
  if (current.getDate() === 1) {
    const prev = new Date(current)
    prev.setMonth(prev.getMonth() - 1)
    return prev
  }
  const firstOfMonth = new Date(current)
  firstOfMonth.setDate(1)
  return firstOfMonth
}

export function formatSnapshotDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function buildPriorityListVariables(input: {
  snapshotDate: string
  filter: TrygroupDashboardPriorityListFilterInput | null
  cursor: string | null | undefined
  limit: number
}): GetTrygroupDashboardPriorityListQueryVariables {
  const variables: GetTrygroupDashboardPriorityListQueryVariables = {
    snapshotDate: input.snapshotDate,
    filter: input.filter,
    limit: input.limit,
  }

  if (input.cursor != null) {
    variables.cursor = input.cursor
  }

  return variables
}

function buildCurrentFilterInput(
  filter: TrygroupPriorityListFilter,
): TrygroupDashboardPriorityListFilterInput | null {
  return buildFilterInput(filter, {
    includeSegments: true,
  })
}

function buildComparisonFilterInput(
  filter: TrygroupPriorityListFilter,
): TrygroupDashboardPriorityListFilterInput | null {
  return buildFilterInput(filter, {
    includeSegments: false,
  })
}

function buildFilterInput(
  filter: TrygroupPriorityListFilter,
  options: {
    includeSegments: boolean
  },
): TrygroupDashboardPriorityListFilterInput | null {
  const input: TrygroupDashboardPriorityListFilterInput = {}

  if (options.includeSegments && filter.segments.length > 0) {
    input.segments = filter.segments.map(toGqlSegment)
  }

  if (filter.userCategoryIds.length > 0) {
    input.userCategoryIds = filter.userCategoryIds
  }

  if (filter.requiresGuidanceOnly) {
    input.requiresGuidanceOnly = true
  }

  if (filter.departmentId != null && filter.departmentId !== '') {
    input.departmentId = filter.departmentId
  }

  const searchKeyword = filter.searchKeyword.trim()
  if (searchKeyword.length > 0) {
    input.searchKeyword = searchKeyword
  }

  if (Object.keys(input).length === 0) {
    return null
  }

  return input
}

function toGqlSegment(segment: TrygroupSegment): GqlTrygroupSegment {
  switch (segment) {
    case 'S':
      return GqlTrygroupSegment.S
    case 'A':
      return GqlTrygroupSegment.A
    case 'B':
      return GqlTrygroupSegment.B
    case 'N':
      return GqlTrygroupSegment.N
  }
}

function mapPriorityListPage(
  current: Apollo.QueryResult<GetTrygroupDashboardPriorityListQuery>,
  comparison: Apollo.QueryResult<GetTrygroupDashboardPriorityListQuery>,
  comparisonSnapshotDate: string,
): TrygroupPriorityListPage {
  const currentData = current.data
  const comparisonData = comparison.data
  if (!currentData || !comparisonData) {
    throw new Error(
      'TrygroupDashboardPriorityListService: priority list query returned no data',
    )
  }

  if (
    comparisonData.trygroupDashboardPriorityList.pageInfo.hasNextPage === true
  ) {
    console.error(
      'TrygroupDashboardPriorityListService: comparison page has next page. Deltas may be incomplete.',
    )
  }

  const comparisonItemsByUserId = new Map(
    comparisonData.trygroupDashboardPriorityList.items.map((item) => [
      item.userId,
      item,
    ]),
  )

  return {
    snapshotDate: currentData.trygroupDashboardPriorityList.snapshotDate,
    comparisonSnapshotDate,
    items: currentData.trygroupDashboardPriorityList.items.map((item) =>
      mapPriorityListItem(item, comparisonItemsByUserId.get(item.userId)),
    ),
    totalCount: currentData.trygroupDashboardPriorityList.totalCount,
    pageInfo: {
      hasNextPage:
        currentData.trygroupDashboardPriorityList.pageInfo.hasNextPage,
      endCursor:
        currentData.trygroupDashboardPriorityList.pageInfo.endCursor ?? null,
    },
    userCategories: currentData.trygroupUserCategories.map((category) => ({
      id: category.id,
      name: category.name,
      active: category.active,
    })),
    departments: currentData.getAdminOrg.departments.map((department) => ({
      id: department.id,
      name: department.name,
    })),
  }
}

function mapPriorityListItem(
  current: GqlPriorityListItem,
  comparison: GqlPriorityListItem | undefined,
): TrygroupPriorityListItem {
  const currentKeyActionScores = mapKeyActionScores(current.keyActionScores)
  const comparisonKeyActionScores = mapKeyActionScores(
    comparison?.keyActionScores,
  )

  return {
    userId: current.userId,
    user: {
      id: current.user.id,
      name: current.user.name,
      email: current.user.email,
    },
    userCategoryId: current.userCategoryId ?? null,
    userCategoryName: current.userCategoryName ?? null,
    segment: mapSegment(current.segment),
    overallScore: current.overallScore ?? null,
    keyActionScores: currentKeyActionScores,
    admissionRate: current.admissionRate ?? null,
    assessmentCount: current.assessmentCount,
    requiresGuidance: current.requiresGuidance,
    deltas: {
      overallScore: calculateDelta(
        current.overallScore,
        comparison?.overallScore,
      ),
      ka1: calculateDelta(
        currentKeyActionScores.ka1,
        comparisonKeyActionScores.ka1,
      ),
      ka2: calculateDelta(
        currentKeyActionScores.ka2,
        comparisonKeyActionScores.ka2,
      ),
      ka3: calculateDelta(
        currentKeyActionScores.ka3,
        comparisonKeyActionScores.ka3,
      ),
      ka4: calculateDelta(
        currentKeyActionScores.ka4,
        comparisonKeyActionScores.ka4,
      ),
      ka5: calculateDelta(
        currentKeyActionScores.ka5,
        comparisonKeyActionScores.ka5,
      ),
      ka6: calculateDelta(
        currentKeyActionScores.ka6,
        comparisonKeyActionScores.ka6,
      ),
      ka7: calculateDelta(
        currentKeyActionScores.ka7,
        comparisonKeyActionScores.ka7,
      ),
      ka8: calculateDelta(
        currentKeyActionScores.ka8,
        comparisonKeyActionScores.ka8,
      ),
      admissionRate: calculateDelta(
        current.admissionRate,
        comparison?.admissionRate,
      ),
    },
  }
}

function mapKeyActionScores(
  keyActionScores: GqlPriorityListItem['keyActionScores'] | undefined | null,
): TrygroupPriorityListKeyActionScores {
  return {
    ka1: keyActionScores?.ka1 ?? null,
    ka2: keyActionScores?.ka2 ?? null,
    ka3: keyActionScores?.ka3 ?? null,
    ka4: keyActionScores?.ka4 ?? null,
    ka5: keyActionScores?.ka5 ?? null,
    ka6: keyActionScores?.ka6 ?? null,
    ka7: keyActionScores?.ka7 ?? null,
    ka8: keyActionScores?.ka8 ?? null,
  }
}

function mapSegment(
  segment: GqlTrygroupSegment | null | undefined,
): TrygroupSegment | null {
  if (segment == null) {
    return null
  }

  switch (segment) {
    case GqlTrygroupSegment.S:
      return 'S'
    case GqlTrygroupSegment.A:
      return 'A'
    case GqlTrygroupSegment.B:
      return 'B'
    case GqlTrygroupSegment.N:
      return 'N'
  }
}

function calculateDelta(
  current: number | null | undefined,
  comparison: number | null | undefined,
): TrygroupPriorityListDelta {
  if (current == null || comparison == null) {
    return {
      value: null,
      direction: 'none',
    }
  }

  const value = Number((current - comparison).toFixed(2))

  if (value > 0) {
    return {
      value,
      direction: 'up',
    }
  }

  if (value < 0) {
    return {
      value,
      direction: 'down',
    }
  }

  return {
    value,
    direction: 'flat',
  }
}
